import { useCallback } from 'react';
import { useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useFormContext } from 'react-hook-form';
import { TagsFormBody } from 'src/components/TagsFormBody';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useGetEventMapConfig } from 'src/contexts/EventMapContext';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosEnumSelect, PosSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { getTextFieldState } from 'src/core/POS/PosTextField';
import { Stack } from 'src/core/ui';
import { useTicketTypes } from 'src/hooks/api/useTicketTypes';
import { SectionContent } from 'src/modals/common';
import { DetailSection } from 'src/modals/common';
import { ContentId } from 'src/utils/constants/contentId';
import { TICKET_TYPE_TO_CID } from 'src/utils/constants/contentIdMaps';
import { getConsecutiveSeatingsForQuantity } from 'src/utils/saleUtils';
import { isSectionTypeGeneralAdmission } from 'src/utils/ticketUtils';
import { ActionOutboxEntityType, Tag } from 'src/WebApiController';
import {
  DeliveryType,
  Event,
  ListingDetails,
  Ticket,
  TicketType,
} from 'src/WebApiController';

import { RetailSaleFormValues } from '../types';
import * as styles from './RetailSaleBuyerStep.css';

export const TicketSection = ({
  maxQuantity,
  deliveryType,
  tickets,
  event,
  listing,
}: {
  maxQuantity?: number;
  deliveryType?: DeliveryType;
  tickets?: Ticket[];
  event?: Event | null;
  listing?: ListingDetails;
}) => {
  const venueMapQuery = useGetEventMapConfig(
    event?.viagId,
    event?.viagId == null || listing?.sectionType != null
  );

  const { watch, register, formState, setValue, clearErrors } =
    useFormContext<RetailSaleFormValues>();

  const quantitySold = watch('quantitySold');
  const ticketType = watch('ticketType');
  const section = watch('section');
  const seatFrom = watch('seatFrom');
  const sectionError = formState.errors.section?.message;
  const quantitySoldError = formState.errors.quantitySold?.message;
  const rowError = formState.errors.row?.message;
  const seatFromError = formState.errors.seatFrom?.message;
  const seatToError = formState.errors.seatTo?.message;

  const { ticketTypes } = useTicketTypes(deliveryType);

  const deliveryTypeToTicketTypesMap = useMemo(() => {
    const ticketTypeOptions = { ...TICKET_TYPE_TO_CID };
    if (deliveryType) {
      if (ticketTypes?.length) {
        Object.keys(ticketTypeOptions).forEach((k) => {
          const tt = k as TicketType;
          if (!ticketTypes.includes(tt)) {
            delete ticketTypeOptions[tt];
          }
        });

        // if ticket type is null or not exists in the options (because feature not allow)
        // default to 1st available ones
        if (ticketType == null || ticketTypeOptions[ticketType] == null) {
          setValue('ticketType', ticketTypes[0]);
        }
      }
    }

    return ticketTypeOptions;
  }, [deliveryType, setValue, ticketType, ticketTypes]);

  const [seatFromToOptions, setSeatFromToOptions] = useState<
    Record<number, number> | undefined
  >();
  const [seatFromOptions, setSeatFromOptions] = useState<
    Record<string, string>
  >({});

  const setSeatTo = useCallback(
    (quantitySold: number, seatFr: string, seatToOrdinal: number) => {
      if (quantitySold < 2) {
        setValue('seatTo', seatFr);
      } else {
        const t = tickets!.find((t) => t.lstOrd === seatToOrdinal);
        if (t) setValue('seatTo', t.seat);
      }
    },
    [setValue, tickets]
  );

  const canAllocateToNonConsecutiveSeats = useMemo(() => {
    let isSeatingGA = false;

    if (listing?.sectionType != null) {
      isSeatingGA = isSectionTypeGeneralAdmission(listing.sectionType);
    } else {
      const sections = venueMapQuery.data?.sections;
      if (sections != null && section != null) {
        isSeatingGA = isSectionTypeGeneralAdmission(
          sections.find(
            (s) => s.name.toLocaleLowerCase() === section?.toLocaleLowerCase()
          )?.sectionType
        );
      }
    }

    return isSeatingGA;
  }, [listing?.sectionType, section, venueMapQuery.data?.sections]);

  // update seatFrom and seatTo options when quantity sold changes
  useEffect(() => {
    const result = getConsecutiveSeatingsForQuantity(
      tickets,
      quantitySold,
      canAllocateToNonConsecutiveSeats
    );
    if (!result || listing?.isSeatSaver) return;

    if (result.maxAvailableQuantity < (quantitySold ?? 0)) {
      // Current quantity sold not valid, so set new max possible quantity sold and quit
      // the next cycle of the use effect will recalculate the options
      setValue('quantitySold', result.maxAvailableQuantity);
      return;
    }

    const newSeatFromToOptions = result.result;

    if (
      tickets?.length &&
      newSeatFromToOptions &&
      !isEqual(newSeatFromToOptions, seatFromToOptions)
    ) {
      setSeatFromToOptions(newSeatFromToOptions);
      const seatFromOrdinals = Object.keys(newSeatFromToOptions);
      const newSeatFromOptions = seatFromOrdinals.reduce(
        (r, c) => {
          const t = tickets!.find((t) => t.lstOrd === parseInt(c));
          r[(t!.lstOrd ?? t!.ord).toString()] = t!.seat;
          return r;
        },
        {} as Record<string, string>
      );

      if (!seatFrom) {
        const seatFr = Object.values(newSeatFromOptions)[0];
        setValue('seatFrom', seatFr);

        const seatToOrdinal =
          newSeatFromToOptions![parseInt(seatFromOrdinals[0])];
        setSeatTo(quantitySold!, seatFr, seatToOrdinal);
      } else {
        // Either the seatFr is no longer valid due to quantity change, or seatTo, need to revalidate both
        const seatFromTicket = tickets!.find((t) => t.seat === seatFrom);

        let seatFr = seatFromTicket!.seat;
        let seatFromOrdinal = seatFromTicket!.lstOrd ?? seatFromTicket!.ord;
        if (!seatFromOrdinals.includes(seatFromOrdinal.toString())) {
          seatFr = Object.values(newSeatFromOptions)[0];
          setValue('seatFrom', seatFr);
          seatFromOrdinal = parseInt(seatFromOrdinals[0]);
        }

        const seatToOrdinal = newSeatFromToOptions![seatFromOrdinal];
        setSeatTo(quantitySold!, seatFr, seatToOrdinal);
      }

      setSeatFromOptions(newSeatFromOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantitySold]);

  const ticketFrom = useMemo(() => {
    return tickets?.find((t) => t.seat === seatFrom);
  }, [tickets, seatFrom]);

  return (
    <DetailSection name={<Content id={ContentId.Tickets} />}>
      <SectionContent numOfColumns={3}>
        <PosFormField
          errors={sectionError}
          label={<Content id={ContentId.TicketType} />}
        >
          <PosEnumSelect
            style={{ width: '100%' }}
            valueOptionsContent={deliveryTypeToTicketTypesMap}
            value={ticketType}
            onChange={(t: TicketType | null) => {
              if (t && t !== ticketType) {
                setValue('ticketType', t);
              }
            }}
          />
        </PosFormField>
        <PosFormField
          errors={sectionError}
          label={<Content id={ContentId.Section} />}
        >
          <PosTextField
            rootProps={{
              state: getTextFieldState(sectionError),
            }}
            disabled={true}
            spellCheck={false}
            {...register('section')}
          />
        </PosFormField>
        <PosFormField label={<Content id={ContentId.Row} />} errors={rowError}>
          <PosTextField
            spellCheck={false}
            disabled={true}
            rootProps={{
              state: getTextFieldState(rowError),
            }}
            {...register('row')}
          />
        </PosFormField>
        <PosFormField
          errors={quantitySoldError}
          label={<Content id={ContentId.Quantity} />}
        >
          <PosTextField
            rootProps={{
              state: getTextFieldState(quantitySoldError),
            }}
            // We disable if editing the Sale - existing sale should not be
            // allowed to edit quantity as that mess up the ticket allocations
            disabled={(maxQuantity ?? 0) <= 0}
            type="number"
            inputMode="numeric"
            value={quantitySold ?? ''}
            onChange={(e) => {
              const v = parseInt(e.target.value);
              if (v > 0 && v <= maxQuantity!) {
                clearErrors('quantitySold');

                if (v !== quantitySold) {
                  setValue('quantitySold', v);
                }
              } else {
                setValue('quantitySold', null);
              }
            }}
          />
        </PosFormField>
        <PosFormField
          label={<Content id={ContentId.SeatFrom} />}
          errors={seatFromError}
        >
          <PosSelect
            disabled={!seatFromToOptions}
            style={{ width: '100%' }}
            hasErrors={Boolean(getTextFieldState(seatFromError))}
            valueOptionsContent={seatFromOptions}
            placeholderText={ContentId.SelectSeatFrom}
            value={ticketFrom?.lstOrd?.toString()}
            onChange={(newSeatOrdinal) => {
              const ord = parseInt(newSeatOrdinal);
              if (ord >= 0) {
                const fromTick = tickets!.find((t) => t.lstOrd === ord);
                const toTick = tickets!.find(
                  (t) => t.lstOrd === seatFromToOptions![ord]
                );

                setValue('seatFrom', fromTick!.seat);
                setValue('seatTo', toTick!.seat);
              }
            }}
          />
        </PosFormField>
        <PosFormField
          label={<Content id={ContentId.SeatTo} />}
          errors={seatToError}
        >
          <PosTextField
            spellCheck={false}
            disabled={true} // This is auto-field based on SeatFrom and Quantity - so always disabled this
            {...register('seatTo')}
          />
        </PosFormField>
      </SectionContent>
    </DetailSection>
  );
};

export const AddNewBuyerForm = () => {
  const requiredMsg = useContent(ContentId.Required);
  const optionalMsg = useContent(ContentId.Optional);

  const { register, watch } = useFormContext<RetailSaleFormValues>();

  const billingAddressSameAsShipping = watch('billingAddressSameAsShipping');

  return (
    <Stack direction="column" gap="m">
      <Stack direction="column" gap="m">
        <span className={styles.newBuyerFormHeader}>
          <Content id={ContentId.AddNewBuyer} />
        </span>
        <SectionContent numOfColumns={2}>
          <PosFormField label={<Content id={ContentId.Name} />}>
            <PosTextField
              placeholder={requiredMsg}
              {...register('buyerName')}
            />
          </PosFormField>
          <PosFormField label={<Content id={ContentId.Email} />}>
            <PosTextField
              placeholder={requiredMsg}
              type="email"
              {...register('buyerEmail')}
            />
          </PosFormField>
          <PosFormField label={<Content id={ContentId.Phone} />}>
            <PosTextField
              placeholder={requiredMsg}
              type="tel"
              {...register('buyerPhone')}
            />
          </PosFormField>
          <PosFormField label={<Content id={ContentId.Company} />}>
            <PosTextField
              placeholder={optionalMsg}
              {...register('buyerCompany')}
            />
          </PosFormField>
        </SectionContent>
      </Stack>

      {/* SHIPPING ADDRESS section */}
      <Stack direction="column" gap="m">
        <span className={styles.newBuyerFormHeader}>
          <Content id={ContentId.ShippingAddress} />
        </span>
        <SectionContent numOfColumns={2}>
          <PosFormField label={<Content id={ContentId.StreetAddress} />}>
            <PosTextField
              placeholder={requiredMsg}
              {...register('shippingStreet')}
            />
          </PosFormField>
          <PosFormField label={<Content id={ContentId.Suite} />}>
            <PosTextField
              placeholder={optionalMsg}
              {...register('shippingSuite')}
            />
          </PosFormField>
          <PosFormField label={<Content id={ContentId.City} />}>
            <PosTextField
              placeholder={requiredMsg}
              {...register('shippingCity')}
            />
          </PosFormField>
          <PosFormField label={<Content id={ContentId.State} />}>
            <PosTextField
              placeholder={requiredMsg}
              {...register('shippingState')}
            />
          </PosFormField>
          <PosFormField label={<Content id={ContentId.Country} />}>
            <PosTextField
              placeholder={requiredMsg}
              {...register('shippingCountry')}
            />
          </PosFormField>
          <PosFormField label={<Content id={ContentId.ZipCode} />}>
            <PosTextField
              placeholder={requiredMsg}
              {...register('shippingZipCode')}
            />
          </PosFormField>
        </SectionContent>
      </Stack>

      {/* BILLING ADDRESS section */}
      <Stack direction="column" gap="m">
        <span className={styles.newBuyerFormHeader}>
          <Content id={ContentId.BillingAddress} />
        </span>
        <SectionContent numOfColumns={1}>
          <div className={styles.checkboxRow}>
            <input
              type="checkbox"
              id="billingAddressSameAsShipping"
              {...register('billingAddressSameAsShipping')}
            />
            <label htmlFor="billingAddressSameAsShipping">
              <Content id={ContentId.SameAsShipping}></Content>
            </label>
          </div>
        </SectionContent>
        {!billingAddressSameAsShipping && (
          <SectionContent numOfColumns={2}>
            <PosFormField label={<Content id={ContentId.StreetAddress} />}>
              <PosTextField
                placeholder={requiredMsg}
                {...register('billingStreet')}
              />
            </PosFormField>
            <PosFormField label={<Content id={ContentId.Suite} />}>
              <PosTextField
                placeholder={optionalMsg}
                {...register('billingSuite')}
              />
            </PosFormField>
            <PosFormField label={<Content id={ContentId.City} />}>
              <PosTextField
                placeholder={requiredMsg}
                {...register('billingCity')}
              />
            </PosFormField>
            <PosFormField label={<Content id={ContentId.State} />}>
              <PosTextField
                placeholder={requiredMsg}
                {...register('billingState')}
              />
            </PosFormField>
            <PosFormField label={<Content id={ContentId.Country} />}>
              <PosTextField
                placeholder={requiredMsg}
                {...register('billingCountry')}
              />
            </PosFormField>
            <PosFormField label={<Content id={ContentId.ZipCode} />}>
              <PosTextField
                placeholder={requiredMsg}
                {...register('billingZipCode')}
              />
            </PosFormField>
          </SectionContent>
        )}
      </Stack>

      <SectionContent numOfColumns={1}>
        <PosFormField label={<Content id={ContentId.Notes} />}>
          <PosTextField
            placeholder={optionalMsg}
            {...register('customerMessage')}
          />
        </PosFormField>
      </SectionContent>
    </Stack>
  );
};

export const SaleTags = () => {
  const { setValue, watch } = useFormContext<RetailSaleFormValues>();
  const tags = watch('tags');

  const onUpdateTags = useCallback(
    (updatedTags: Tag[]) => {
      setValue('tags', updatedTags);
    },
    [setValue]
  );

  return (
    <TagsFormBody
      canEditTags
      tagsCurrentEntity={tags ?? []}
      onTagsUpdate={onUpdateTags}
      entityType={ActionOutboxEntityType.Sale}
    />
  );
};
